import { Link } from "react-router-dom";
import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import { getDecodedToken } from "../../auth";
import { capitalizeWords } from "../../functions";

function StaffAppliedLeave() {
  const user = getDecodedToken();
  const [staffAppliedLeave, setStaffAppliedLeave] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split(/[\/\-]/).map(Number);
    return new Date(year, month - 1, day);
  };

  const getLeaveDays = (fromDate, toDate) => {
    const from = parseDate(fromDate);
    const to = parseDate(toDate);
    if (!from || !to || isNaN(from) || isNaN(to)) return "—";
    const diffTime = to.getTime() - from.getTime();
    return diffTime >= 0
      ? Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
      : "—";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${server}/GetAllAppliedStaffLeave`, {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        });

        let data = response.data?.staffLeaveDetails || [];
        data = data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        setStaffAppliedLeave(data);
      } catch (err) {
        console.error("Error fetching leave data", err);
        setStaffAppliedLeave([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleUpdateLeaveStatus = async (leaveId, newStatus) => {
    try {
      let remarks = "";

      if (newStatus === "rejected") {
        const { value: remarkValue, isConfirmed } = await MySwal.fire({
          title: "Reject Leave Request",
          icon: "info",
          input: "textarea",
          inputLabel: "Remarks",
          inputPlaceholder: "Enter the reason for rejecting this leave...",
          inputAttributes: {
            "aria-label": "Enter the reason for rejecting this leave",
          },
          showCancelButton: true,
          confirmButtonText: "Reject Leave",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#dc3545",
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return "Please enter a remark before rejecting.";
            }
          },
        });

        if (!isConfirmed) return;
        remarks = remarkValue.trim();
      } else {
        const confirmAction = await MySwal.fire({
          title: `Are you sure you want to ${newStatus}?`,
          text: "Do you want to update applied leave status?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes, proceed",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#198754",
        });

        if (!confirmAction.isConfirmed) return;
      }

      const response = await axios.put(
        `${server}/UpdateAppliedStaffLeave`,
        {
          leaveObjectId: leaveId,
          status: newStatus,
          ...(newStatus === "rejected" && { remarks }),
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.status === 200) {
        toast.success(`Leave ${newStatus}`);
        setStaffAppliedLeave((prev) =>
          prev.map((item) =>
            item._id === leaveId
              ? {
                  ...item,
                  status: newStatus,
                  ...(newStatus === "rejected" && { remarks }),
                }
              : item,
          ),
        );
      } else {
        toast.error("Failed to update leave status.");
      }
    } catch (error) {
      toast.error("Something went wrong while updating.");
    }
  };

  // Filter
  const filteredRecords = staffAppliedLeave.filter((staff) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(staff).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // Calculate pagination
  // Sort by bus number (numerically, not lexically)
  filteredRecords.sort((a, b) => {
    const numA = a.busNumber?.match(/\d+/)?.[0] ?? 0;
    const numB = b.busNumber?.match(/\d+/)?.[0] ?? 0;
    return Number(numA) - Number(numB);
  });

  // Then slice for pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // 📘 Function to export data to Excel
  const exportToExcel = (data, fileName, sheetName) => {
    if (!data || data.length === 0) {
      toast.warn("No data to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  // 📘 Function to export all leaves in one file, with multiple sheets
  const exportAllReports = () => {
    if (!staffAppliedLeave || staffAppliedLeave.length === 0) {
      toast.warn("No leave records found");
      return;
    }

    // Prepare clean structured data for Excel
    const data = staffAppliedLeave.map((staff) => ({
      StaffID: staff.staffId,
      Name: staff.name,
      LeaveType: staff.casual
        ? "Casual"
        : staff.medical
          ? "Medical"
          : staff.lwt
            ? "Leave Without Pay"
            : "Other",
      FromDate: staff.fromDate,
      ToDate: staff.toDate,
      TotalDays: getLeaveDays(staff.fromDate, staff.toDate),
      Reason: staff.reason,
      Status: staff.status,
      Remarks: staff.remarks || "",
      AppliedOn: new Date(staff.createdAt).toLocaleDateString(),
    }));

    // Create one worksheet for all data
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "All Applied Leaves");

    // Download Excel file
    XLSX.writeFile(workbook, "All_Applied_Leaves.xlsx");

    toast.success("Report Generated");
  };

  // 📘 Individual filters
  const exportMonthWise = () => {
    const grouped = {};
    staffAppliedLeave.forEach((staff) => {
      const month = new Date(staff.fromDate).toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      if (!grouped[month]) grouped[month] = [];
      grouped[month].push(staff);
    });

    const workbook = XLSX.utils.book_new();
    Object.entries(grouped).forEach(([month, data]) => {
      const sheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, sheet, month);
    });
    XLSX.writeFile(workbook, "MonthWise_LeaveReport.xlsx");
  };

  const exportStaffWise = () => {
    const grouped = {};
    staffAppliedLeave.forEach((staff) => {
      const name = staff.name || staff.staffId;
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(staff);
    });

    const workbook = XLSX.utils.book_new();
    Object.entries(grouped).forEach(([name, data]) => {
      const sheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, sheet, name.slice(0, 30));
    });
    XLSX.writeFile(workbook, "StaffWise_LeaveReport.xlsx");
  };

  const exportApproved = () =>
    exportToExcel(
      staffAppliedLeave.filter((s) => s.status === "approved"),
      "Approved_Leaves",
      "Approved",
    );

  const exportRejected = () =>
    exportToExcel(
      staffAppliedLeave.filter((s) => s.status === "rejected"),
      "Rejected_Leaves",
      "Rejected",
    );

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((staff, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Staff ID</span>{" "}
                              &mdash; {staff?.staffId || "—"}
                              <br />
                              <span className="text-dark">Full Name</span>{" "}
                              &mdash; {staff.fullName || "NA"}
                              <hr className="w-25 my-2" />
                              <span className="text-dark">Leave Type</span>{" "}
                              &mdash;{" "}
                              {staff?.casual
                                ? "Casual Leave"
                                : staff?.medical
                                  ? "Medical Leave"
                                  : staff?.lwt
                                    ? "Leave Without Pay"
                                    : "NA"}
                              <br />
                              <span className="text-dark">From Date</span>{" "}
                              &mdash; {staff?.fromDate}
                              <br />
                              <span className="text-dark">
                                To Date
                              </span> &mdash; {staff?.toDate}
                              <br />
                              <span className="text-dark">Total Days</span>{" "}
                              &mdash; {staff?.totalAppliedLeaves}
                              <br />
                              <span className="text-dark">
                                Reason
                              </span> &mdash;{" "}
                              {staff.reason
                                .split(" ")
                                .reduce(
                                  (acc, word) => {
                                    const lastLine = acc[acc.length - 1];
                                    const currentLineLength =
                                      lastLine.join(" ").length;
                                    if (
                                      currentLineLength + word.length + 1 >
                                      50
                                    ) {
                                      acc.push([word]);
                                    } else {
                                      lastLine.push(word);
                                    }

                                    return acc;
                                  },
                                  [[]],
                                )
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                              <hr className="w-25 my-2" />
                              <span className="text-dark">Document</span>{" "}
                              &mdash;{" "}
                              {staff?.documents ? (
                                <a
                                  href={staff.documents}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View File
                                </a>
                              ) : (
                                "No Document"
                              )}
                              <br />
                              <span className="text-dark">
                                Date
                              </span> &mdash;{" "}
                              <span className="text-uppercase">
                                {new Date(staff?.createdAt).toLocaleString() ||
                                  "NA"}
                              </span>
                              {staff.status?.toLowerCase() === "rejected" &&
                                staff.remarks && (
                                  <>
                                    <br />
                                    <span className="text-dark">
                                      Remarks
                                    </span>{" "}
                                    &mdash; <span>{staff.remarks}</span>
                                  </>
                                )}
                            </td>
                            <td>
                              {staff.status.toLowerCase() === "pending" ? (
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <span
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                    onClick={() =>
                                      handleUpdateLeaveStatus(
                                        staff._id,
                                        "approved",
                                      )
                                    }
                                  >
                                    Approve
                                  </span>
                                  <span
                                    className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                    onClick={() =>
                                      handleUpdateLeaveStatus(
                                        staff._id,
                                        "rejected",
                                      )
                                    }
                                  >
                                    Decline
                                  </span>
                                </div>
                              ) : (
                                <span
                                  className={`badge px-4 rounded-3 ${
                                    staff.status === "approved"
                                      ? "bg-success-soft text-success"
                                      : "bg-danger-soft text-danger"
                                  }`}
                                >
                                  {capitalizeWords(staff.status)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Human Resource</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Staff Applied Leave
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Staff Applied Leave
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.5 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  onClick={exportAllReports}
                                >
                                  All Applied Leave
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  onClick={exportMonthWise}
                                >
                                  Month Wise
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  onClick={exportStaffWise}
                                >
                                  Staff Wise
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  onClick={exportApproved}
                                >
                                  Leave Approved
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  onClick={exportRejected}
                                >
                                  Leave Rejected
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default StaffAppliedLeave;
